import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LogIn, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";

export const UserMenu = () => {
  const { user, loading, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (loading) return <div className="w-10 h-10 kp-glass animate-pulse" />;

  if (!user) {
    return (
      <button data-testid="google-signin-button" onClick={login} className="kp-btn kp-btn-ghost !py-2.5 !px-4 hidden sm:inline-flex">
        <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.7-4-13.6-9.6l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
        Sign in
      </button>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button data-testid="user-menu-button" onClick={() => setOpen(!open)} className="flex items-center gap-2 kp-glass pl-1 pr-3 h-10">
        {user.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 object-cover" /> : <span className="w-8 h-8 bg-[#FF2A55] flex items-center justify-center font-display text-xs">{user.name?.[0]}</span>}
        <span data-testid="user-name" className="hidden md:inline text-xs font-mono max-w-[110px] truncate">{user.name}</span>
        <ChevronDown size={12} className="text-[#64748B]" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} data-testid="user-menu" className="absolute right-0 mt-2 w-56 kp-glass p-2 z-50">
            <div className="px-3 py-2 border-b border-white/[0.06]">
              <div className="font-display text-sm font-semibold truncate">{user.name}</div>
              <div className="text-[0.65rem] font-mono text-[#64748B] truncate">{user.email}</div>
            </div>
            <button data-testid="user-logout-button" onClick={() => { setOpen(false); logout(); }} className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm text-[#94A3B8] hover:text-white hover:bg-white/[0.04] transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <span className="sr-only"><LogIn size={0} /></span>
    </div>
  );
};
